"use client";
// src/services/simplified/rateLimitManager.ts - Rate limiting service

export interface RateLimitConfig {
  windowMs: number;
  maxRequests: number;
}

export interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  resetTime: number;
  retryAfter?: number;
}

interface RateLimitEntry {
  count: number;
  resetTime: number;
  blocked: boolean;
  blockedUntil?: number;
}

/**
 * Rate limiting with predefined configurations
 */
export class RateLimitManager {
  private static readonly CONFIGS = {
    AUTH: { windowMs: 3 * 60 * 1000, maxRequests: 5 },        // 5 attempts in 3 minutes
    API_USER: { windowMs: 60 * 1000, maxRequests: 100 },      // 100 requests per minute
    API_ADMIN: { windowMs: 60 * 1000, maxRequests: 200 },     // 200 requests per minute
    UPLOAD: { windowMs: 60 * 1000, maxRequests: 10 },         // 10 uploads per minute
    EMAIL: { windowMs: 60 * 60 * 1000, maxRequests: 10 },     // 10 emails per hour
  } as const;

  private rateLimits = new Map<string, RateLimitEntry>();
  private totalRequests = 0;
  private blockedRequests = 0;

  /**
   * Check rate limit for authentication endpoints
   */
  checkAuth(identifier: string): RateLimitResult {
    return this.checkLimit(identifier, RateLimitManager.CONFIGS.AUTH);
  }

  /**
   * Check rate limit for user API requests
   */
  checkUserApi(identifier: string): RateLimitResult {
    return this.checkLimit(identifier, RateLimitManager.CONFIGS.API_USER);
  }

  /**
   * Check rate limit for admin API requests
   */
  checkAdminApi(identifier: string): RateLimitResult {
    return this.checkLimit(identifier, RateLimitManager.CONFIGS.API_ADMIN);
  }

  /**
   * Check rate limit for upload endpoints
   */
  checkUpload(identifier: string): RateLimitResult {
    return this.checkLimit(identifier, RateLimitManager.CONFIGS.UPLOAD);
  }

  /**
   * Check rate limit for email endpoints
   */
  checkEmail(identifier: string): RateLimitResult {
    return this.checkLimit(identifier, RateLimitManager.CONFIGS.EMAIL);
  }

  /**
   * Core rate limiting logic - simplified without complex branching
   */
  private checkLimit(identifier: string, config: RateLimitConfig): RateLimitResult {
    const now = Date.now();
    let entry = this.rateLimits.get(identifier);

    // Initialize or reset if window expired
    if (!entry || now >= entry.resetTime) {
      entry = {
        count: 0,
        resetTime: now + config.windowMs,
        blocked: false,
      };
      this.rateLimits.set(identifier, entry);
    }

    // Check if currently blocked
    if (entry.blocked && entry.blockedUntil && now < entry.blockedUntil) {
      this.blockedRequests++;
      return {
        allowed: false,
        remaining: 0,
        resetTime: entry.resetTime,
        retryAfter: Math.ceil((entry.blockedUntil - now) / 1000),
      };
    }

    // Increment and check limit
    entry.count++;
    this.totalRequests++;

    const remaining = Math.max(0, config.maxRequests - entry.count);
    const exceeded = entry.count > config.maxRequests;

    if (exceeded) {
      entry.blocked = true;
      entry.blockedUntil = entry.resetTime;
      this.blockedRequests++;
    }

    return {
      allowed: !exceeded,
      remaining,
      resetTime: entry.resetTime,
      retryAfter: exceeded ? Math.ceil((entry.resetTime - now) / 1000) : undefined,
    };
  }

  /**
   * Manually block an identifier
   */
  blockIdentifier(identifier: string, durationMs: number): void {
    const now = Date.now();
    const entry = this.rateLimits.get(identifier) || {
      count: 0,
      resetTime: now + durationMs,
      blocked: false,
    };

    entry.blocked = true;
    entry.blockedUntil = now + durationMs;
    this.rateLimits.set(identifier, entry);
  }

  /**
   * Unblock an identifier
   */
  unblockIdentifier(identifier: string): boolean {
    const entry = this.rateLimits.get(identifier);
    if (entry) {
      entry.blocked = false;
      delete entry.blockedUntil;
      return true;
    }
    return false;
  }

  /**
   * Reset rate limit for an identifier
   */
  resetRateLimit(identifier: string): boolean {
    return this.rateLimits.delete(identifier);
  }

  /**
   * Get current status for an identifier
   */
  getStatus(identifier: string): {
    exists: boolean;
    count: number;
    blocked: boolean;
    resetTime?: number;
    blockedUntil?: number;
  } {
    const entry = this.rateLimits.get(identifier);
    
    if (!entry) {
      return { exists: false, count: 0, blocked: false };
    }

    return {
      exists: true,
      count: entry.count,
      blocked: entry.blocked,
      resetTime: entry.resetTime,
      blockedUntil: entry.blockedUntil,
    };
  }

  /**
   * Clean up expired entries
   */
  cleanup(): number {
    const now = Date.now();
    let removed = 0;

    for (const [identifier, entry] of this.rateLimits.entries()) {
      if (now >= entry.resetTime && (!entry.blockedUntil || now >= entry.blockedUntil)) {
        this.rateLimits.delete(identifier);
        removed++;
      }
    }

    return removed;
  }

  /**
   * Get basic statistics
   */
  getStats(): {
    totalRequests: number;
    blockedRequests: number;
    activeEntries: number;
    blockedCount: number;
  } {
    const now = Date.now();
    let blockedCount = 0;

    for (const entry of this.rateLimits.values()) {
      if (entry.blocked && entry.blockedUntil && now < entry.blockedUntil) {
        blockedCount++;
      }
    }

    return {
      totalRequests: this.totalRequests,
      blockedRequests: this.blockedRequests,
      activeEntries: this.rateLimits.size,
      blockedCount,
    };
  }

  /**
   * Clear all rate limits
   */
  clear(): number {
    const count = this.rateLimits.size;
    this.rateLimits.clear();
    this.totalRequests = 0;
    this.blockedRequests = 0;
    return count;
  }
}

// Export singleton instance
export const rateLimitManager = new RateLimitManager(); 
